import React from "react";
import { useTranslation } from "react-i18next";
import { LandingPageLayout } from "../shared/layout";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Heart, Users, Zap } from "lucide-react";

export function AboutPageScreen() {
  const { t } = useTranslation();
  
  return (
    <LandingPageLayout>
      <div className="py-20 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4">
          {/* Hero Section */}
          <div className="text-center max-w-3xl mx-auto mb-20">
            <Badge variant="outline" className="mb-4">{t("about.badge")}</Badge>
            <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-5xl mb-6">
              {t("about.title")}
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
              {t("about.subtitle")}
            </p>
          </div>

          {/* Mission Section */}
          <div className="grid md:grid-cols-2 gap-12 items-center mb-24">
            <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl p-8 h-80 flex items-center justify-center relative overflow-hidden">
               <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20"></div>
               <Heart className="h-32 w-32 text-primary opacity-20 animate-pulse" />
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-4">{t("about.missionTitle")}</h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">
                {t("about.missionDesc1")}
              </p>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                {t("about.missionDesc2")}
              </p>
            </div>
          </div>

          {/* Values Section */}
          <div className="grid md:grid-cols-3 gap-8 mb-24">
            <Card className="border-none shadow-lg bg-gray-50 dark:bg-gray-800">
              <CardContent className="pt-8 text-center">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-xl font-bold mb-2">{t("about.value1Title")}</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {t("about.value1Desc")}
                </p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg bg-gray-50 dark:bg-gray-800">
              <CardContent className="pt-8 text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold mb-2">{t("about.value2Title")}</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {t("about.value2Desc")}
                </p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg bg-gray-50 dark:bg-gray-800">
              <CardContent className="pt-8 text-center">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="h-6 w-6 text-yellow-600" />
                </div>
                <h3 className="text-xl font-bold mb-2">{t("about.value3Title")}</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {t("about.value3Desc")}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Team Section */}
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-12">{t("about.teamTitle")}</h2>
            <div className="flex justify-center">
              <Card className="border-none shadow-none bg-transparent max-w-sm">
                <CardContent className="flex flex-col items-center pt-6">
                  <Avatar className="h-32 w-32 mb-4 ring-4 ring-primary/10">
                    <AvatarFallback className="text-2xl bg-primary text-primary-foreground">E</AvatarFallback>
                  </Avatar>
                  <h3 className="text-xl font-bold">{t("about.founderName")}</h3>
                  <p className="text-primary font-medium mb-4">{t("about.founderRole")}</p>
                  <p className="text-gray-600 dark:text-gray-400 text-center">
                    {t("about.founderBio")}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </LandingPageLayout>
  );
}
